import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useRuntimeConfig } from "../config/RuntimeConfigContext.jsx";
import { useSession } from "../auth/SessionContext.jsx";
import { login } from "../auth/authApi.js";
import "./login.css";

const GENERIC_LOGIN_ERROR = "เข้าสู่ระบบไม่สำเร็จ กรุณาตรวจสอบข้อมูลอีกครั้ง";

function resolveRedirectTarget(location) {
  const from = location.state?.from;
  if (!from?.pathname) {
    return "/backoffice";
  }
  return `${from.pathname}${from.search ?? ""}`;
}

/**
 * Real /login screen: production-grade split layout (brand context on the
 * left, login form on the right), calling the real POST /api/auth/login
 * through the runtime-configured authBaseUrl - never a hardcoded URL.
 * Every claim in the security note below matches what the backend actually
 * enforces (audit log, temporary lockout, 8-hour session timeout) - no
 * MFA/IP-allowlist claims, since neither exists yet.
 */
export default function LoginPage() {
  const config = useRuntimeConfig();
  const { isAuthenticated, signIn } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  if (isAuthenticated) {
    return <Navigate to="/backoffice" replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await login(identifier, password);
      signIn(result.token);
      navigate(resolveRedirectTarget(location), { replace: true });
    } catch {
      // Deliberately generic, regardless of the real failure reason - the
      // backend's own audit log (WmxLoginAttempts) carries the real detail.
      setErrorMessage(GENERIC_LOGIN_ERROR);
      setIsSubmitting(false);
    }
  }

  return (
    <div className="wx-login-screen">
      <section className="wx-login-brand" aria-hidden="true">
        <div className="wx-login-brand-inner">
          <div className="wx-login-brand-top">
            <div className="wx-login-logo">WX</div>
            <div className="wx-login-brand-name">
              <strong>Workmate X</strong>
              <span>Operations · V0.1</span>
            </div>
          </div>

          <div className="wx-login-pill">
            <span className="wx-login-pulse" />
            ระบบปฏิบัติการแรงงานต่างประเทศ · หลายเอเจนต์
          </div>

          <h1 className="wx-login-heading">แรงงาน เอเจนต์ และนายจ้าง — บนระบบเดียวที่ควบคุมได้</h1>
          <p className="wx-login-lede">
            แพลตฟอร์ม backoffice สำหรับติดตามความปลอดภัย footstep, SOS, zone, face check-in,
            agent accountability และงานในไทยของแรงงานหลักแสนคน
          </p>

          <div className="wx-login-scope-title">สิทธิ์หลักทั้งหมดบนแพลตฟอร์ม</div>
          <div className="wx-login-chips">
            <span className="wx-login-chip wx-login-chip--active">Admin</span>
            <span className="wx-login-chip">Super Agent</span>
            <span className="wx-login-chip">Agent</span>
            <span className="wx-login-chip">Employer</span>
          </div>

          <div className="wx-login-brand-footer">
            <span className="wx-login-status">
              <span className="wx-login-status-dot" />
              All systems operational
            </span>
            <span>© 2026 บริษัท เวิร์กเมต เอ็กซ์ จำกัด</span>
          </div>
        </div>
      </section>

      <section className="wx-login-form-panel">
        <div className="wx-login-form-center">
          <div className="wx-login-card">
            <div className="wx-login-tabs" role="tablist" aria-label="โหมดการเข้าสู่ระบบ">
              <button type="button" className="wx-login-tab wx-login-tab--active" role="tab" aria-selected="true">
                เข้าสู่ระบบ
              </button>
              <button
                type="button"
                className="wx-login-tab"
                role="tab"
                aria-selected="false"
                disabled
                title="ยังไม่เปิดใช้งานการสมัครพาร์ทเนอร์ในระบบนี้"
              >
                สมัครพาร์ทเนอร์
              </button>
            </div>

            <h2 className="wx-login-card-heading">เข้าสู่ระบบ Workmate X</h2>
            <p className="wx-login-card-subcopy">ใช้เบอร์มือถือหรืออีเมลที่ลงทะเบียนไว้กับ {config.appName}</p>

            <form className="wx-login-form" onSubmit={handleSubmit} noValidate>
              <div className="wx-login-field">
                <div className="wx-login-label-row">
                  <label className="wx-login-label" htmlFor="wx-login-identifier">
                    เบอร์มือถือ / อีเมล
                  </label>
                </div>
                <div className="wx-login-input-shell">
                  <svg className="wx-login-input-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <input
                    id="wx-login-identifier"
                    name="identifier"
                    type="text"
                    autoComplete="username"
                    placeholder="081 234 5678 หรือ email"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="wx-login-field">
                <div className="wx-login-label-row">
                  <label className="wx-login-label" htmlFor="wx-login-password">
                    รหัสผ่าน
                  </label>
                </div>
                <div className="wx-login-input-shell">
                  <input
                    id="wx-login-password"
                    name="password"
                    type={isPasswordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="wx-login-toggle-visibility"
                    onClick={() => setIsPasswordVisible((visible) => !visible)}
                    aria-label={isPasswordVisible ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                  >
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      {isPasswordVisible ? (
                        <>
                          <path d="M3 3l18 18" />
                          <path d="M10.6 5.1A9.8 9.8 0 0 1 12 5c6.5 0 10 7 10 7a15.5 15.5 0 0 1-3.6 4.3M6.3 6.3C3.5 8.1 2 12 2 12s3.5 7 10 7a9.8 9.8 0 0 0 3.2-.5" />
                          <path d="M9.9 9.9a3 3 0 1 0 4.2 4.2" />
                        </>
                      ) : (
                        <>
                          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              {errorMessage && (
                <p className="wx-login-error" role="alert">
                  {errorMessage}
                </p>
              )}

              <button type="submit" className="wx-login-submit" disabled={isSubmitting}>
                <span>{isSubmitting ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}</span>
                {!isSubmitting && (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14" />
                    <path d="m13 6 6 6-6 6" />
                  </svg>
                )}
              </button>
            </form>

            <div className="wx-login-security-note">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              </svg>
              <span>
                ป้องกันด้วย audit log ทุกครั้งที่เข้าสู่ระบบ, ระงับบัญชีชั่วคราวเมื่อพยายามเข้าสู่ระบบผิดซ้ำ
                และ session timeout อัตโนมัติหลังไม่มีการใช้งาน 8 ชั่วโมง
              </span>
            </div>
          </div>
        </div>

        <footer className="wx-login-footer">
          <span>workmatex.co.th · Operations</span>
          <span className="wx-login-footer-links">
            <a href={`mailto:${config.supportContact}`}>ติดต่อฝ่ายปฏิบัติการ</a>
          </span>
        </footer>
      </section>
    </div>
  );
}
